import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, ChevronDown, Users, Filter, ArrowUpDown, ArrowDown, ArrowUp, MoreHorizontal, CheckCircle2, Clock } from 'lucide-react';
interface VenturesProps {
  userRole: 'Program Manager' | 'Founder';
}
type VentureStatus = 'active' | 'completed';
type SortDirection = 'asc' | 'desc' | null;
type SortField = 'name' | 'team' | 'credits' | 'weeks' | null;
interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatarUrl: string;
}
interface Venture {
  id: string;
  name: string;
  status: VentureStatus;
  phase: 'Problem' | 'Position' | 'Pilot';
  team: TeamMember[];
  creditsUsed: number;
  creditsTotal: number;
  weeksActive: number;
  lastActivity: string;
}
const Ventures: React.FC<VenturesProps> = ({
  userRole
}) => {
  // State for sorting and filtering
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<VentureStatus | 'all'>('all');
  // Mock data for ventures
  const [ventures, setVentures] = useState<Venture[]>([{
    id: 'v1',
    name: 'CardioSense',
    status: 'active',
    phase: 'Problem',
    team: [{
      id: 'u1',
      name: 'Alex Johnson',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/32.jpg'
    }, {
      id: 'u2',
      name: 'Maria Garcia',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/44.jpg'
    }, {
      id: 'u3',
      name: 'David Kim',
      role: 'Technical Lead',
      avatarUrl: 'https://randomuser.me/api/portraits/men/11.jpg'
    }],
    creditsUsed: 720,
    creditsTotal: 1000,
    weeksActive: 8,
    lastActivity: '2 days ago'
  }, {
    id: 'v2',
    name: 'MedBridge AI',
    status: 'active',
    phase: 'Position',
    team: [{
      id: 'u4',
      name: 'Sarah Chen',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/63.jpg'
    }, {
      id: 'u5',
      name: 'Michael Rodriguez',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/42.jpg'
    }],
    creditsUsed: 450,
    creditsTotal: 1000,
    weeksActive: 6,
    lastActivity: 'Yesterday'
  }, {
    id: 'v3',
    name: 'NeuroNote',
    status: 'active',
    phase: 'Pilot',
    team: [{
      id: 'u6',
      name: 'James Wilson',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/22.jpg'
    }, {
      id: 'u7',
      name: 'Emily Davis',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/17.jpg'
    }, {
      id: 'u8',
      name: 'Robert Taylor',
      role: 'Clinical Advisor',
      avatarUrl: 'https://randomuser.me/api/portraits/men/91.jpg'
    }, {
      id: 'u9',
      name: 'Jessica Brown',
      role: 'Developer',
      avatarUrl: 'https://randomuser.me/api/portraits/women/28.jpg'
    }],
    creditsUsed: 850,
    creditsTotal: 1000,
    weeksActive: 12,
    lastActivity: 'Today'
  }, {
    id: 'v4',
    name: 'PharmaFlow',
    status: 'active',
    phase: 'Problem',
    team: [{
      id: 'u10',
      name: 'Daniel Martinez',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/55.jpg'
    }, {
      id: 'u11',
      name: 'Sophia Lee',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/33.jpg'
    }],
    creditsUsed: 320,
    creditsTotal: 1000,
    weeksActive: 4,
    lastActivity: '5 days ago'
  }, {
    id: 'v5',
    name: 'VitalSync',
    status: 'active',
    phase: 'Position',
    team: [{
      id: 'u12',
      name: 'Thomas Anderson',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/79.jpg'
    }, {
      id: 'u13',
      name: 'Olivia White',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/8.jpg'
    }, {
      id: 'u14',
      name: 'William Clark',
      role: 'Technical Lead',
      avatarUrl: 'https://randomuser.me/api/portraits/men/34.jpg'
    }],
    creditsUsed: 560,
    creditsTotal: 1000,
    weeksActive: 7,
    lastActivity: '1 week ago'
  }, {
    id: 'v6',
    name: 'CareConnect',
    status: 'active',
    phase: 'Problem',
    team: [{
      id: 'u15',
      name: 'Elizabeth Harris',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/52.jpg'
    }, {
      id: 'u16',
      name: 'Christopher Lewis',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/3.jpg'
    }],
    creditsUsed: 280,
    creditsTotal: 1000,
    weeksActive: 3,
    lastActivity: '3 days ago'
  }, {
    id: 'v7',
    name: 'MediTrack',
    status: 'completed',
    phase: 'Pilot',
    team: [{
      id: 'u17',
      name: 'Jennifer Walker',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/71.jpg'
    }, {
      id: 'u18',
      name: 'Andrew Scott',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/18.jpg'
    }],
    creditsUsed: 980,
    creditsTotal: 1000,
    weeksActive: 16,
    lastActivity: '1 month ago'
  }, {
    id: 'v8',
    name: 'HealthGuard',
    status: 'completed',
    phase: 'Pilot',
    team: [{
      id: 'u19',
      name: 'Kevin Thompson',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/62.jpg'
    }, {
      id: 'u20',
      name: 'Amanda Nelson',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/26.jpg'
    }, {
      id: 'u21',
      name: 'Brian Adams',
      role: 'Developer',
      avatarUrl: 'https://randomuser.me/api/portraits/men/41.jpg'
    }],
    creditsUsed: 950,
    creditsTotal: 1000,
    weeksActive: 14,
    lastActivity: '2 months ago'
  }, {
    id: 'v9',
    name: 'PharmAssist',
    status: 'completed',
    phase: 'Pilot',
    team: [{
      id: 'u22',
      name: 'Rachel Green',
      role: 'Founder',
      avatarUrl: 'https://randomuser.me/api/portraits/women/39.jpg'
    }, {
      id: 'u23',
      name: 'Steven Cooper',
      role: 'Co-founder',
      avatarUrl: 'https://randomuser.me/api/portraits/men/29.jpg'
    }],
    creditsUsed: 1000,
    creditsTotal: 1000,
    weeksActive: 20,
    lastActivity: '3 months ago'
  }]);
  // Sort function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if already sorting by this field
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortField(null);
      }
    } else {
      // Start with ascending sort for new field
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} className="ml-1" data-id="element-1267" />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={16} className="ml-1" data-id="element-1268" /> : <ArrowDown size={16} className="ml-1" data-id="element-1269" />;
  };
  // Filter and sort ventures
  const filteredAndSortedVentures = [...ventures]
  // First filter by status if needed
  .filter(venture => {
    if (statusFilter === 'all') return true;
    return venture.status === statusFilter;
  })
  // Then filter by search query
  .filter(venture => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return venture.name.toLowerCase().includes(query) || venture.team.some(member => member.name.toLowerCase().includes(query));
  })
  // Then sort
  .sort((a, b) => {
    if (!sortField || !sortDirection) {
      // Default sort: active first, then alphabetically by name
      if (a.status !== b.status) {
        return a.status === 'active' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    }
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'team':
        comparison = a.team.length - b.team.length;
        break;
      case 'credits':
        comparison = a.creditsUsed - b.creditsUsed;
        break;
      case 'weeks':
        comparison = a.weeksActive - b.weeksActive;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  return <div className="w-full bg-gray-50" data-id="element-1270">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-1271">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1272">
          <div data-id="element-1273">
            <h1 className="text-xl font-semibold text-gray-800" data-id="element-1274">Ventures</h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1275">
              Manage and track all ventures in your program
            </p>
          </div>
          {userRole === 'Program Manager' && <div className="mt-4 md:mt-0" data-id="element-1276">
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center transition-colors" data-id="element-1277">
                <Plus size={16} className="mr-2" data-id="element-1278" /> New Venture
              </button>
            </div>}
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3" data-id="element-1279">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1280">
          <div className="flex items-center space-x-4" data-id="element-1281">
            <div className="relative w-64" data-id="element-1282">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1283" />
              <input type="text" placeholder="Search ventures or team members..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1284" />
            </div>
            <div className="relative" data-id="element-1285">
              <button className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" onClick={() => {
              setStatusFilter(statusFilter === 'all' ? 'active' : statusFilter === 'active' ? 'completed' : 'all');
            }} data-id="element-1286">
                <Filter size={16} data-id="element-1287" />
                <span data-id="element-1288">
                  Status:{' '}
                  {statusFilter === 'all' ? 'All' : statusFilter === 'active' ? 'Active' : 'Completed'}
                </span>
                <ChevronDown size={16} data-id="element-1289" />
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500" data-id="element-1290">
            Showing {filteredAndSortedVentures.length} ventures
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="px-6 py-4 overflow-x-auto" data-id="element-1291">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1292">
          <thead className="bg-gray-50" data-id="element-1293">
            <tr data-id="element-1294">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1295">
                <button className="flex items-center focus:outline-none" onClick={() => handleSort('name')} data-id="element-1296">
                  Venture {getSortIcon('name')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1297">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1298">
                <button className="flex items-center focus:outline-none" onClick={() => handleSort('team')} data-id="element-1299">
                  Team {getSortIcon('team')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1300">
                <button className="flex items-center focus:outline-none" onClick={() => handleSort('credits')} data-id="element-1301">
                  Credit Usage {getSortIcon('credits')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1302">
                <button className="flex items-center focus:outline-none" onClick={() => handleSort('weeks')} data-id="element-1303">
                  Weeks Active {getSortIcon('weeks')}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b" data-id="element-1304">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1305">
            {filteredAndSortedVentures.map(venture => <tr key={venture.id} className="hover:bg-gray-50" data-id="element-1306">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1307">
                  <div className="flex items-center" data-id="element-1308">
                    <div data-id="element-1309">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1310">
                        {venture.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1311">
                        Phase: {venture.phase}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1312">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${venture.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1313">
                    {venture.status === 'active' ? <>
                        <Clock size={12} className="mr-1" data-id="element-1314" /> Active
                      </> : <>
                        <CheckCircle2 size={12} className="mr-1" data-id="element-1315" /> Completed
                      </>}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1316">
                  <div className="flex items-center" data-id="element-1317">
                    <div className="flex -space-x-2 mr-2" data-id="element-1318">
                      {venture.team.slice(0, 3).map((member, index) => <img key={member.id} src={member.avatarUrl} alt={member.name} className="w-7 h-7 rounded-full border-2 border-white" title={member.name} data-id="element-1319" />)}
                      {venture.team.length > 3 && <div className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs text-gray-600" data-id="element-1320">
                          +{venture.team.length - 3}
                        </div>}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1321">
                      {venture.team.length} members
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1322">
                  <div className="w-32" data-id="element-1323">
                    <div className="flex items-center justify-between mb-1" data-id="element-1324">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1325">
                        {venture.creditsUsed.toLocaleString()} /{' '}
                        {venture.creditsTotal.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1326">
                        {Math.round(venture.creditsUsed / venture.creditsTotal * 100)}
                        %
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full" data-id="element-1327">
                      <div className={`h-1.5 rounded-full ${venture.creditsUsed / venture.creditsTotal > 0.8 ? 'bg-red-500' : venture.creditsUsed / venture.creditsTotal > 0.6 ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{
                    width: `${venture.creditsUsed / venture.creditsTotal * 100}%`
                  }} data-id="element-1328"></div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1329">
                  <div className="text-sm text-gray-900" data-id="element-1330">
                    {venture.weeksActive} weeks
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1331">
                    Last active: {venture.lastActivity}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1332">
                  <Link to={`/ventures/${venture.id}`} className="text-blue-600 hover:text-blue-900 mr-4" data-id="element-1333">
                    View
                  </Link>
                  <button className="text-gray-400 hover:text-gray-600" data-id="element-1334">
                    <MoreHorizontal size={16} data-id="element-1335" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};
export default Ventures;